import React, { useMemo } from 'react';
import PriorityPicker, {
	PriorityPickerValue,
} from '../../../Base/Components/PriorityPicker/PriorityPicker';
import { RequestPriority } from '../../Enums/RequestPriority.enum';
import { Theme } from '../../../../Enums/Theme.enum';
import {
	usePriorityCaseMutation,
	useTermItemsQuery,
} from '../../../../graphql/graphql';
import Loading, { LoadingSize } from '../../../../Components/Loading';
import useApiClient from '../../../Base/Hooks/useApiClient';
import { MonitoringCenterCase } from '../MonitoringCenterListTable/MonitoringCenterListTable';

interface Props {
	item: MonitoringCenterCase;
}

const PRIORITY_CONFIG = [
	{
		key: RequestPriority.HIGH,
		title: 'component.monitoringCenter.priority.high',
		color: Theme.RED,
	},
	{
		key: RequestPriority.MEDIUM,
		title: 'component.monitoringCenter.priority.medium',
		color: Theme.ORANGE,
	},
	{
		key: RequestPriority.LOW,
		title: 'component.monitoringCenter.priority.low',
		color: Theme.GREEN,
	},
	{
		key: RequestPriority.NONE,
		title: 'component.monitoringCenter.priority.none',
		color: Theme.GREY,
	},
];

const MonitoringCenterPriorityPickerColumn: React.FC<Props> = ({ item }) => {
	const [setCasePriority] = usePriorityCaseMutation();
	const { displayError } = useApiClient();

	const { data, isLoading } = useTermItemsQuery({
		termType: 'case_priority',
		take: 999,
	});

	const priorityOptions: PriorityPickerValue[] = useMemo(() => {
		const termItems = data?.termItems?.data ?? [];

		const idByValue = Object.fromEntries(
			termItems
				.filter(item => item?.value && item.id)
				.map(item => [item.value, item.id]),
		) as Record<string, string>;

		return PRIORITY_CONFIG.flatMap(({ key, title, color }) => {
			const id = idByValue[key];

			if (!id) {
				return [];
			}

			return [
				{
					id,
					key,
					title,
					color,
				},
			];
		});
	}, [data]);

	const handleChangePriority = async (
		priorityPickerItem: PriorityPickerValue,
	) => {
		try {
			await setCasePriority({
				input: {
					caseId: item.id,
					priorityId: priorityPickerItem.id,
				},
			}).unwrap();
		} catch (e) {
			displayError(e);
		}
	};

	if (isLoading) {
		return <Loading size={LoadingSize.SMALL} />;
	}

	return (
		<PriorityPicker
			options={priorityOptions}
			value={
				priorityOptions.find(option => option.key === item.priority?.value) ||
				priorityOptions.find(item => item.key === 'none')
			}
			onPick={handleChangePriority}
		/>
	);
};

export default MonitoringCenterPriorityPickerColumn;
